import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "../../../hooks";
import { api } from "../../../utils/api";
import { unstable_batchedUpdates } from "react-dom";
import { trackPromise } from "react-promise-tracker";
import { AuthContext, DependentsContext, GroupOwnerContext, PaycheckContext, ThemeContext } from "../../../contexts";
import { LogoSettings } from "../../../types/logoSettings.types";
import { PortalLoader } from "../../shared/loader/portalLoader/PortalLoader";

const Redirection = (): JSX.Element => {
	const query = useQuery();
	const history = useHistory();
	const { groupOwner, setGroupOwner } = useContext(GroupOwnerContext);
	const { setMember } = useContext(AuthContext);
	const { setPaycheck } = useContext(PaycheckContext);
	const { setTheme, theme } = useContext(ThemeContext);
	const { setDependents } = useContext(DependentsContext);
	const [logoSettings, setLogoSettings] = useState<LogoSettings | null>(null);
	const [terminatedEmployee, setTerminatedEmployee] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true); //  controls PortalLoader
	const redirectDone = useRef(false); // tracks if redirect finished
	const isLoadingRef = useRef(true);
	const fetchInitData = async () => {
		const jwt = localStorage.getItem("@jwt_member");
		if (!jwt || jwt === "null") return;

		const groupOwnerToken = localStorage.getItem("@jwt_group_owner");
		const memberPortalInitData: any =
			localStorage.getItem("@role") === "DEPENDENT"
				? await trackPromise(api.pages.getDependentPortalInitData())
				: await trackPromise(api.pages.getMemberPortalInitData(groupOwnerToken));

		unstable_batchedUpdates(() => {
			if (memberPortalInitData.member.employee_status === "RELIEVED") {
				setTerminatedEmployee(true);
			}
			setMember({ ...memberPortalInitData.member });
			if (memberPortalInitData.paycheck) setPaycheck({ ...memberPortalInitData.paycheck });
			if (memberPortalInitData.groupOwner) setGroupOwner({ ...memberPortalInitData.groupOwner });
			if (memberPortalInitData.dependents) setDependents([...memberPortalInitData.dependents]);
			setTheme(
				memberPortalInitData.theme ?? {
					...theme,
					group_number: memberPortalInitData.member.group_number
				}
			);
			setLogoSettings({ ...memberPortalInitData.logoSettings });
		});
	};

	const verifyRedirection = useCallback(async () => {
		if (query && query.get("id") && query.get("token")) {
			const result = await api.redirection.redirectUser(query.get("id") as string, query.get("token") as string);
			localStorage.clear();
			if (query.get("is_emp")) {
				localStorage.removeItem("@jwt_group_owner");
			} else {
				localStorage.setItem("@jwt_group_owner", query.get("token") as string);
			}
			localStorage.setItem("@jwt_member", result.token as string);
			localStorage.setItem("@role", result.role);
			sessionStorage.setItem("hasSeenLoader", "true"); //  so App.tsx skips its loader

			await fetchInitData();

			redirectDone.current = true; // mark redirect complete

			// Only navigate if loader has already finished
			if (!isLoadingRef.current) {
				// always fresh
				history.push("/");
			}
		}
	}, [query, history]);

	useEffect(() => {
		localStorage.clear();
		verifyRedirection();
	}, [verifyRedirection]);

	// Called when PortalLoader finishes its animation
	const handleLoadingComplete = useCallback(() => {
		isLoadingRef.current = false; //  update ref
		setIsLoading(false); //  update state for re-render
		if (redirectDone.current) {
			history.push("/");
		}
	}, [history]);

	if (isLoading) {
		return <PortalLoader onLoadingComplete={handleLoadingComplete} duration={5000} />;
	}

	return <>Redirecting...</>;
};

export default Redirection;
